/**
 * Hook absorbing the load/loading/error preamble that every per-app page
 * in the portal repeated. Replaces the ~25-line `interface State` +
 * `useState` + `useCallback` + `useEffect` + `if (loading)/error/null`
 * ladder with a single hook call.
 *
 * Two variants:
 *
 *   useAppPage(slug)
 *     For pages that only need the app row (Settings, AppDetail).
 *
 *   useAppPageWith(slug, fetchPayload, deps?)
 *     For pages with one extra fetch (Health, Activity, Usage, Versions,
 *     Share, Secrets, Schedules). Races the payload against the app fetch
 *     via Promise.all on first load; on reload, refetches both.
 *
 * Both return `{ app, payload, loading, error, reload, setApp, setPayload }`.
 * `app.is_manager` is normalized to a real `boolean` (the wire type is
 * `boolean | undefined`); the awkward `=== true` coercion at the call
 * site goes away.
 *
 * Mutation handlers should call `reload()` to refresh after a write —
 * same contract as the old `await load()` pattern, but reload uses a
 * functional setState so the existing payload stays on screen during
 * the refetch (no skeleton flash mid-mutation).
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, apiMessage } from '../api/client.js';
import type { AppSummary } from '../api/types.js';

/** AppSummary with is_manager narrowed to a non-optional boolean. */
export type NormalizedApp = Omit<AppSummary, 'is_manager'> & { is_manager: boolean };

interface AppPageState<P> {
  loading: boolean;
  app?: NormalizedApp;
  payload?: P;
  error?: string;
}

export interface AppPageResult<P> {
  /** True only during the initial load (no `app` yet). Subsequent reloads keep `app` on screen. */
  loading: boolean;
  app?: NormalizedApp;
  payload?: P;
  error?: string;
  reload: () => Promise<void>;
  /** Local update of the app row — for callers that want to avoid a redundant GET after a mutation. */
  setApp: (update: (app: NormalizedApp) => NormalizedApp) => void;
  /** Local update of the payload — same idea. */
  setPayload: (update: (payload: P) => P) => void;
}

function appUrl(slug: string): string {
  return `/api/apps/${encodeURIComponent(slug)}`;
}

function normalizeApp(app: AppSummary): NormalizedApp {
  return { ...app, is_manager: app.is_manager === true };
}

export function useAppPage(slug: string | undefined): AppPageResult<never> {
  return useAppPageWith<never>(slug, undefined);
}

export function useAppPageWith<P>(
  slug: string | undefined,
  fetchPayload: ((slug: string) => Promise<P>) | undefined,
  deps: ReadonlyArray<unknown> = [],
): AppPageResult<P> {
  const [state, setState] = useState<AppPageState<P>>({ loading: true });

  const load = useCallback(async () => {
    if (!slug) {
      setState({ loading: false, error: 'Missing app slug.' });
      return;
    }
    // Keep the prior payload on screen during a reload — the empty-state
    // branch (`if (loading && !app)`) only fires on the initial mount.
    setState((prev) => {
      const { error: _err, ...rest } = prev;
      void _err;
      return { ...rest, loading: true };
    });
    try {
      const appPromise = api.get<AppSummary>(appUrl(slug));
      if (fetchPayload) {
        const [app, payload] = await Promise.all([appPromise, fetchPayload(slug)]);
        setState({ loading: false, app: normalizeApp(app), payload });
      } else {
        const app = await appPromise;
        setState({ loading: false, app: normalizeApp(app) });
      }
    } catch (err) {
      setState((prev) => ({ ...prev, loading: false, error: apiMessage(err) }));
    }
    // The `deps` param lets the hook re-fire when a non-slug input changes
    // (e.g. range selectors). Consumer is responsible for stable identities.
  }, [slug, ...deps]);

  useEffect(() => {
    void load();
  }, [load]);

  const setApp = useCallback((update: (app: NormalizedApp) => NormalizedApp) => {
    setState((prev) => (prev.app ? { ...prev, app: update(prev.app) } : prev));
  }, []);

  const setPayload = useCallback((update: (payload: P) => P) => {
    setState((prev) =>
      prev.payload !== undefined ? { ...prev, payload: update(prev.payload) } : prev,
    );
  }, []);

  return {
    loading: state.loading && state.app === undefined,
    ...(state.app !== undefined ? { app: state.app } : {}),
    ...(state.payload !== undefined ? { payload: state.payload } : {}),
    ...(state.error !== undefined ? { error: state.error } : {}),
    reload: load,
    setApp,
    setPayload,
  };
}

/**
 * Re-export so call sites can type their `catch (err)` arms without
 * pulling `ApiError` from a different module than the hook.
 */
export { ApiError };
